import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Store as StoreIcon, Search, Package, MapPin, MessageCircle } from 'lucide-react';
import { getAllStores, getProductsByStore } from '../lib/firebaseService';

export default function StoresPage() {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadStores();
  }, []);

  const loadStores = async () => {
    try {
      setLoading(true);
      const storesData = await getAllStores();
      
      // Get product count for each store
      const storesWithProducts = await Promise.all(
        storesData.map(async (store) => {
          const products = await getProductsByStore(store.id);
          return {
            ...store,
            products_count: products.length,
            products_preview: products.slice(0, 4)
          };
        })
      );
      
      setStores(storesWithProducts);
    } catch (error) {
      console.error('Mağazalar yüklənə bilmədi:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredStores = stores.filter(store =>
    store.store_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    store.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-white" data-testid="stores-page">
      {/* Grid Pattern Background */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
      </div>

      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-gray-200 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <span className="text-2xl font-bold text-black">I Veb</span>
            </Link>
            <div className="flex gap-3">
              <Link to="/login">
                <Button variant="outline" className="border-2 border-black hover:bg-black hover:text-white">Daxil ol</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-black hover:bg-gray-800 text-white">Mağaza aç</Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-black">Mağazalar</h1>
            <p className="text-lg text-gray-600 mb-8">
              Azərbaycandakı online mağazaları kəşf edin
            </p>
            
            {/* Search */}
            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Mağaza axtar..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12 text-lg border-2 border-gray-200 focus:border-black focus:ring-black"
                data-testid="store-search-input"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stores Grid */}
      <main className="container mx-auto px-4 py-8">
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Mağazalar yüklənir...</p>
          </div>
        ) : filteredStores.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <StoreIcon className="w-10 h-10 text-gray-400" />
            </div>
            <p className="text-xl font-medium mb-2 text-black">Mağaza tapılmadı</p>
            <p className="text-gray-500 mb-6">
              {searchQuery ? 'Axtarış nəticəsi yoxdur' : 'Hələ heç bir mağaza qeydiyyatdan keçməyib'}
            </p>
            <Link to="/register">
              <Button className="bg-black hover:bg-gray-800 text-white">İlk mağazanı aç</Button>
            </Link>
          </div>
        ) : (
          <>
            <p className="text-gray-600 mb-6 font-medium">{filteredStores.length} mağaza tapıldı</p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredStores.map((store) => (
                <Link
                  key={store.id}
                  to={`/store/${store.id}`}
                  className="group bg-white rounded-2xl border-2 border-gray-200 overflow-hidden hover:border-black hover:shadow-2xl transition-all duration-300"
                  data-testid={`store-card-${store.id}`}
                >
                  {/* Store Header */}
                  <div className="p-6 border-b-2 border-gray-100">
                    <div className="flex items-center gap-4">
                      {store.logo_url ? (
                        <img
                          src={store.logo_url}
                          alt={store.store_name}
                          className="w-16 h-16 rounded-xl object-cover border-2 border-gray-200"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-black rounded-xl flex items-center justify-center">
                          <StoreIcon className="w-8 h-8 text-white" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-xl font-bold truncate group-hover:text-black transition-colors text-gray-900">
                          {store.store_name}
                        </h3>
                        <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                          <Package className="w-4 h-4" />
                          <span>{store.products_count} məhsul</span>
                        </div>
                      </div>
                    </div>
                    {store.description && (
                      <p className="text-sm text-gray-600 mt-3 line-clamp-2">
                        {store.description}
                      </p>
                    )}
                  </div>

                  {/* Products Preview */}
                  {store.products_preview && store.products_preview.length > 0 && (
                    <div className="p-4 bg-gray-50">
                      <div className="grid grid-cols-4 gap-2">
                        {store.products_preview.map((product) => (
                          <div key={product.id} className="aspect-square rounded-lg overflow-hidden bg-white border-2 border-gray-200">
                            {product.image_url ? (
                              <img
                                src={product.image_url}
                                alt={product.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Package className="w-6 h-6 text-gray-400" />
                              </div>
                            )}
                          </div>
                        ))}
                        {[...Array(Math.max(0, 4 - store.products_preview.length))].map((_, i) => (
                          <div key={`empty-${i}`} className="aspect-square rounded-lg bg-gray-100" />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Store Footer */}
                  <div className="p-4 flex items-center justify-between bg-gray-50/50">
                    <span className="text-sm text-black font-medium group-hover:underline">
                      Mağazaya bax →
                    </span>
                    {store.whatsapp_number && (
                      <div className="flex items-center gap-1 text-green-600 text-sm">
                        <MessageCircle className="w-4 h-4" />
                        <span>WhatsApp</span>
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </main>

      {/* CTA Section */}
      <section className="bg-black text-white py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff12_1px,transparent_1px),linear-gradient(to_bottom,#ffffff12_1px,transparent_1px)] bg-[size:24px_24px]"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Siz də mağaza açın!</h2>
          <p className="text-gray-300 mb-8 max-w-xl mx-auto text-lg">
            Bir neçə dəqiqə ərzində öz online mağazanızı yaradın və müştərilərinizə çatın.
          </p>
          <Link to="/register">
            <Button size="lg" className="px-12 bg-white text-black hover:bg-gray-100">
              Pulsuz Başla
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t-2 border-gray-200 py-8">
        <div className="container mx-auto px-4">
          <p className="text-center text-gray-600">&copy; 2025 I Veb. Bütün hüquqlar qorunur.</p>
        </div>
      </footer>
    </div>
  );
}
